/**
 * Cashiers Page - Card Layout
 * Refactored with Clean Architecture pattern
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Wallet, Edit, Trash2, PlayCircle, StopCircle, ShoppingCart, Loader2 } from 'lucide-react';

import CashierModal from './CashierModal';
import OpenCashierModal from './OpenCashierModal';
import CloseCashierModal from './CloseCashierModal';
import './styles.css';

export default function Cashiers() {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useInstance();
  
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCashier, setEditingCashier] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  // Open/Close modals
  const [openModalVisible, setOpenModalVisible] = useState(false);
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [selectedCashier, setSelectedCashier] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/cashiers');
      setCashiers(res.data);
    } catch (err) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingCashier?.id) {
        await api.put(`/cashiers/${editingCashier.id}`, data);
      } else {
        await api.post('/cashiers', data);
      }
      toast.success(t('cashiers.saveSuccess'));
      setModalOpen(false);
      setEditingCashier(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingCashier to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingCashier(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/cashiers/${deleteId}`);
      toast.success(t('cashiers.deleteSuccess'));
      setDeleteId(null);
      loadData();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  const handleOpenCashier = async (data) => {
    try {
      await api.post(`/cashiers/${selectedCashier.id}/open`, data);
      toast.success(t('cashiers.openSuccess'));
      setOpenModalVisible(false);
      setSelectedCashier(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleCloseCashier = async (data) => {
    try {
      const res = await api.post(`/cashiers/${selectedCashier.id}/close`, data);
      let message = t('cashiers.closeSuccess');
      if (res.data.difference > 0) {
        message += ` Sobra de ${formatCurrency(res.data.difference)}`;
      } else if (res.data.difference < 0) {
        message += ` Diferença de ${formatCurrency(Math.abs(res.data.difference))}`;
      }
      toast.success(message);
      setCloseModalVisible(false);
      setSelectedCashier(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="cashiers-page" data-testid="cashiers-page">
      <div className="cashiers-header">
        <div className="cashiers-header-title">
          <h1>
            <Wallet className="h-6 w-6" />
            {t('cashiers.title')}
          </h1>
          <p>{cashiers.length} caixas cadastrados</p>
        </div>
        <Button 
          onClick={() => { setEditingCashier(null); setModalOpen(true); }}
          data-testid="new-cashier-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('cashiers.newCashier')}
        </Button>
      </div>

      <div className="cashiers-grid">
        {cashiers.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-muted-foreground">
              {t('common.noResults')}
            </CardContent>
          </Card>
        ) : (
          cashiers.map(cashier => (
            <Card 
              key={cashier.id} 
              className={`cashier-card ${cashier.status === 'open' ? 'cashier-card-open' : ''}`}
              data-testid={`cashier-card-${cashier.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`cashier-icon ${cashier.status === 'open' ? 'cashier-icon-open' : ''}`}>
                    <Wallet className="h-6 w-6" />
                  </div>
                  <Badge variant={cashier.status === 'open' ? 'default' : 'secondary'}>
                    {cashier.status === 'open' ? t('cashiers.open') : t('cashiers.closed')}
                  </Badge>
                </div>
                
                <h3 className="font-semibold text-lg mb-1">{cashier.name}</h3>
                
                {cashier.status === 'open' ? (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('cashiers.initialAmount')}</span>
                      <span className="font-mono">{formatCurrency(cashier.initial_amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('cashiers.currentAmount')}</span>
                      <span className="font-mono font-semibold text-primary">{formatCurrency(cashier.current_amount)}</span>
                    </div>
                    {cashier.opened_at && (
                      <p className="text-xs text-muted-foreground">
                        Aberto em {formatDate(cashier.opened_at, true)}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mb-4">
                    {cashier.is_active ? 'Disponível para abertura' : 'Desativado'}
                  </p>
                )}

                <div className="flex flex-col gap-2">
                  {cashier.status === 'open' ? (
                    <>
                      <Button asChild className="w-full">
                        <Link to={`/pdv/${cashier.id}`}>
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          Abrir PDV
                        </Link>
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setSelectedCashier(cashier);
                            setCloseModalVisible(true);
                          }}
                        >
                          <StopCircle className="h-4 w-4 mr-1" />
                          Fechar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingCashier(cashier);
                            setModalOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setSelectedCashier(cashier);
                          setOpenModalVisible(true);
                        }}
                      >
                        <PlayCircle className="h-4 w-4 mr-1" />
                        Abrir Caixa
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditingCashier(cashier);
                          setModalOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(cashier.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <CashierModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        cashier={editingCashier}
        onSave={handleSave}
        loading={saving}
      />

      <OpenCashierModal
        open={openModalVisible}
        onOpenChange={setOpenModalVisible}
        cashier={selectedCashier}
        onConfirm={handleOpenCashier}
      />

      <CloseCashierModal
        open={closeModalVisible}
        onOpenChange={setCloseModalVisible}
        cashier={selectedCashier}
        onConfirm={handleCloseCashier}
        formatCurrency={formatCurrency}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('cashiers.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
